import { useCallback, useEffect, useRef, useState } from "react";
import { getEngine } from "@/lib/engineSingleton";
import { checkMicAvailability } from "@/lib/micCheck";
import { micTimeoutError, showMicHelp } from "@/lib/micHelp";
import { listInputDevices } from "@/lib/creatorVocal";
import { frameLevels, toDb } from "@/lib/audioAnalysis";

const RAW = { echoCancellation: false, noiseSuppression: false, autoGainControl: false };

/** A live microphone for the setup wizard: permission, device list, real input levels, timed measurements, monitoring. */
export default function useMicStream(initialDevice) {
  const ctx = getEngine().ctx;
  const [state, setState] = useState("idle");
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(initialDevice || "");
  const [levels, setLevels] = useState({ peakDb: -120, rmsDb: -120 });
  const [clipped, setClipped] = useState(false);
  const nodes = useRef({});
  const frames = useRef(null);

  const close = useCallback(() => {
    const n = nodes.current;
    n.stream?.getTracks().forEach((t) => t.stop());
    try { n.src?.disconnect(); n.monitor?.disconnect(); } catch { /* already gone */ }
    nodes.current = {};
  }, []);
  useEffect(() => close, [close]);

  const open = useCallback(async (id = deviceId) => {
    close();
    const pre = checkMicAvailability();
    if (pre) { setState("error"); showMicHelp(pre, () => open(id)); return; }
    setState("asking");
    let stream;
    try {
      const ask = navigator.mediaDevices.getUserMedia({ audio: { ...RAW, ...(id ? { deviceId: { exact: id } } : {}) } });
      stream = await Promise.race([ask, new Promise((_, rej) => setTimeout(() => rej(micTimeoutError()), 25000))]);
    } catch (e) { setState("error"); showMicHelp(e, () => open(id)); return; }
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    src.connect(analyser);
    nodes.current = { stream, src, analyser };
    setDeviceId(stream.getAudioTracks()[0]?.getSettings?.().deviceId || id || "");
    setDevices(await listInputDevices());
    setClipped(false);
    setState("live");
  }, [close, ctx, deviceId]);

  useEffect(() => {
    if (state !== "live") return undefined;
    const buf = new Float32Array(2048);
    let raf = 0;
    let last = 0;
    const tick = (now) => {
      const a = nodes.current.analyser;
      if (a) {
        a.getFloatTimeDomainData(buf);
        const lv = frameLevels(buf);
        if (lv.clip) setClipped(true);
        frames.current?.push(lv);
        if (now - last > 50) { setLevels(lv); last = now; }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [state]);

  /** Collect real levels for `sec` seconds → { peakDb, noiseDb (quietest 10 %), rmsDb }. */
  const measure = useCallback(async (sec) => {
    frames.current = [];
    await new Promise((r) => setTimeout(r, sec * 1000));
    const f = frames.current || [];
    frames.current = null;
    if (!f.length) return null;
    const rms = f.map((x) => x.rmsDb).sort((a, b) => a - b);
    const lin = f.map((x) => 10 ** (x.rmsDb / 20));
    return { peakDb: Math.max(...f.map((x) => x.peakDb)), noiseDb: rms[Math.floor(rms.length * 0.1)], rmsDb: toDb(Math.sqrt(lin.reduce((a, v) => a + v * v, 0) / lin.length)) };
  }, []);

  /** Hear yourself (headphones only — the caller enforces that). */
  const setMonitor = useCallback((on, gain = 0.8) => {
    const n = nodes.current;
    if (!n.src) return;
    if (on && !n.monitor) { n.monitor = ctx.createGain(); n.src.connect(n.monitor).connect(ctx.destination); }
    if (n.monitor) n.monitor.gain.value = on ? gain : 0;
  }, [ctx]);

  return { state, devices, deviceId, levels, clipped, open, close, measure, setMonitor, resetClip: () => setClipped(false) };
}
